import AutorenewOutlinedIcon from "@mui/icons-material/AutorenewOutlined";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import SubjectOutlinedIcon from "@mui/icons-material/SubjectOutlined";
import { skipToken } from "@reduxjs/toolkit/query";
import type { KeyboardEvent } from "react";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router";

import InfoTooltip from "@/components/common/InfoTooltip";
import { adminUiCopy } from "@/copy/adminUiCopy";
import { useAdminPermissions } from "@/hooks/useAdminPermissions";
import {
  useCreateAdminV2ApprovalMutation,
  useGetAdminV2MeQuery,
  useGetAdminV2RevenueDisputeDetailQuery,
  useGetAdminV2RevenueDisputesQuery,
  useGetAdminV2RevenueSubscriptionsQuery,
  useGetAdminV2RevenueTransactionDetailQuery,
  useGetAdminV2RevenueTransactionsQuery,
  useGetAdminV2RevenueWebhookEventDetailQuery,
  useGetAdminV2RevenueWebhookEventsQuery,
} from "@/store/api/baseApi";
import type {
  AdminV2BillingTransactionProjection,
  AdminV2RevenueRefundReason,
  AdminV2RevenueWebhookEvent,
} from "@/types/adminV2";
import {
  buildAdminRevenueScopedPath,
  resolveAdminRevenueAccessScope,
} from "@/utils/adminRevenueAccessScope";
import FrontendLoggerFactory from "@/utils/logging/FrontendLoggerFactory";
import { RevenueWorkspaceChrome } from "@/vnext/routes/revenue/RevenueWorkspaceChrome";
import { SettingsWorkspaceChrome } from "@/vnext/routes/settings/SettingsWorkspaceChrome";
import {
  formatMaskedProviderReference,
  formatVNextCurrency,
  formatVNextDateTime,
  formatVNextLabel,
  formatVNextNumber,
} from "@/vnext/shared/formatters";

import "@/vnext/routes/revenue/revenueBillingMake.css";

const THIRTY_DAY_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;
const PAGE_SIZE = 10;
const REFUND_REQUEST_TYPE = "revenue.transaction.refund";
const REFUND_APPROVAL_FIXED_CACHE_KEY =
  "revenue-billing-refund-approval-request";
const REFUND_APPROVAL_PENDING_MESSAGE =
  "A refund approval request is still being submitted. Wait for it to finish before requesting another.";
const UUID_LIKE_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const REFUNDABLE_TRANSACTION_STATUSES = new Set([
  "succeeded",
  "partially_refunded",
]);
const REFUND_EXECUTION_UNAVAILABLE_REASON =
  "Refund execution is unavailable in this Billing overview. It requires refund access and an approved request ID in the approved refund workflow.";
const INVALID_ORGANISATION_SCOPE_MESSAGE =
  "Enter a valid organisation UUID or clear the invalid scope to load billing data.";
const EMPTY_WEBHOOK_EVENTS: AdminV2RevenueWebhookEvent[] = [];
const INTERNAL_WEBHOOK_ERROR_PATTERN =
  /\b(?:sql|postgres|constraint|foreign key|database|violates|23503|23505)\b/i;
const REFUND_REASON_OPTIONS: Array<{
  value: AdminV2RevenueRefundReason;
  label: string;
}> = [
  {
    value: "requested_by_customer",
    label: "Requested by customer",
  },
  {
    value: "duplicate",
    label: "Duplicate",
  },
  {
    value: "fraudulent",
    label: "Fraudulent",
  },
];
const logger = FrontendLoggerFactory.getLogger().withComponent(
  "RevenueBillingRoute",
);

type RevenueBillingPeriodPreset =
  "month" | "quarter" | "ytd" | "annual" | "all-time";
type RevenueBillingRouteMode = "billing" | "settings-webhook-logs";

interface RevenueBillingRouteProps {
  mode?: RevenueBillingRouteMode;
}

const PERIOD_PRESETS: Array<{
  label: string;
  value: RevenueBillingPeriodPreset;
}> = [
  { label: "Month", value: "month" },
  { label: "Quarter", value: "quarter" },
  { label: "YTD", value: "ytd" },
  { label: "Annual", value: "annual" },
  { label: "All-time", value: "all-time" },
];

const parseIsoDate = (value: string | null | undefined) => {
  if (!value) {
    return null;
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const isOnOrAfter = (
  value: string | null | undefined,
  minimumDate: Date | null,
) => {
  if (!minimumDate) {
    return true;
  }

  const parsed = parseIsoDate(value);
  return parsed ? parsed.getTime() >= minimumDate.getTime() : false;
};

const getPeriodStart = (
  periodPreset: RevenueBillingPeriodPreset,
  now: Date,
) => {
  const year = now.getUTCFullYear();
  const month = now.getUTCMonth();

  switch (periodPreset) {
    case "month":
      return new Date(Date.UTC(year, month, 1));
    case "quarter":
      return new Date(Date.UTC(year, Math.floor(month / 3) * 3, 1));
    case "ytd":
      return new Date(Date.UTC(year, 0, 1));
    case "annual":
      return new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
    case "all-time":
      return null;
    default:
      return null;
  }
};

const formatTransactionAccessibleName = (
  transaction: AdminV2BillingTransactionProjection,
) =>
  [
    `Select transaction ${formatMaskedProviderReference(transaction.id)}`,
    `status ${formatVNextLabel(transaction.status)}`,
    `amount ${formatVNextCurrency(
      transaction.amountCents,
      transaction.currency,
    )}`,
    `occurred ${formatVNextDateTime(transaction.occurredAt)}`,
    `organisation ${formatMaskedProviderReference(transaction.organisationId)}`,
  ].join("; ");

const getStatusTone = (status: string | null | undefined) => {
  const normalizedStatus = status?.trim().toLowerCase() ?? "";

  if (
    normalizedStatus.includes("fail") ||
    normalizedStatus.includes("cancel") ||
    normalizedStatus.includes("dispute")
  ) {
    return "danger";
  }

  if (
    normalizedStatus.includes("process") ||
    normalizedStatus.includes("queue") ||
    normalizedStatus.includes("needs")
  ) {
    return "warning";
  }

  return "success";
};

const makeDetailItems = (items: Array<{ label: string; value: string }>) => (
  <dl className="billing-make-key-value-list">
    {items.map((item) => (
      <div className="billing-make-key-value-list__row" key={item.label}>
        <dt>{item.label}</dt>
        <dd>{item.value}</dd>
      </div>
    ))}
  </dl>
);

const getWebhookRawFailure = (
  event: Pick<AdminV2RevenueWebhookEvent, "lastError">,
): string => event.lastError?.trim() ?? "";

const isWebhookBillingRecordMappingFailure = (rawLastError: string): boolean =>
  rawLastError.length > 0 &&
  (/^\d+$/.test(rawLastError) ||
    INTERNAL_WEBHOOK_ERROR_PATTERN.test(rawLastError));

const formatWebhookFailureReason = (
  event: Pick<AdminV2RevenueWebhookEvent, "lastError" | "status">,
): string => {
  const rawLastError = getWebhookRawFailure(event);
  if (!rawLastError) {
    return event.status === "failed"
      ? "No failure reason recorded."
      : "No failure recorded.";
  }

  if (isWebhookBillingRecordMappingFailure(rawLastError)) {
    return "Could not match this webhook to a billing record.";
  }

  return "Failure details are hidden because they may include payment provider data.";
};

const formatWebhookRowFailureSummary = (
  event: Pick<AdminV2RevenueWebhookEvent, "lastError" | "status">,
): string => {
  const rawLastError = getWebhookRawFailure(event);
  const failureReason = formatWebhookFailureReason(event);
  if (failureReason === "Could not match this webhook to a billing record.") {
    return "Check billing record mapping";
  }

  if (rawLastError) {
    return "Check webhook retry details";
  }

  return failureReason;
};

const getWebhookNextCheck = (
  event: Pick<AdminV2RevenueWebhookEvent, "lastError" | "status">,
): string => {
  const rawLastError = getWebhookRawFailure(event);
  if (event.status === "failed") {
    if (isWebhookBillingRecordMappingFailure(rawLastError)) {
      return "Check the event reference and organisation scope before retrying.";
    }

    if (rawLastError) {
      return "Use the event reference and billing record to investigate before retrying.";
    }

    return "Check the event reference before retrying.";
  }

  if (event.status === "processed") {
    return "No action needed unless the billing record looks wrong.";
  }

  return "Wait for processing, then refresh this log.";
};

const makeWebhookSafeEventSummary = (event: AdminV2RevenueWebhookEvent) => (
  <div className="vnext-field">
    <span className="vnext-field__label">Safe event summary</span>
    {makeDetailItems([
      {
        label: "Event",
        value: event.eventType,
      },
      {
        label: "Delivery status",
        value: formatVNextLabel(event.status),
      },
      {
        label: "Attempts",
        value: formatVNextNumber(event.attempts),
      },
      {
        label: "Event reference",
        value: formatMaskedProviderReference(event.stripeEventId),
      },
      {
        label: "Signature verified",
        value: formatVNextDateTime(event.signatureVerifiedAt),
      },
      {
        label: "Processed",
        value: formatVNextDateTime(event.processedAt),
      },
      {
        label: "Organisation",
        value: event.organisationId
          ? formatMaskedProviderReference(event.organisationId)
          : "Global",
      },
      {
        label: "Failure reason",
        value: formatWebhookFailureReason(event),
      },
      {
        label: "Next check",
        value: getWebhookNextCheck(event),
      },
    ])}
  </div>
);

const InlineHelpLabel = (args: {
  label: string;
  title: string;
  description: string;
}) => (
  <span
    style={{
      alignItems: "center",
      display: "inline-flex",
      gap: 4,
    }}
  >
    <span>{args.label}</span>
    <InfoTooltip description={args.description} title={args.title} />
  </span>
);

function BillingEmptyState({ message }: { message: string }) {
  return (
    <div className="billing-make-empty-state">
      <InfoOutlinedIcon sx={{ fontSize: 14 }} />
      <p>{message}</p>
    </div>
  );
}

const parseRefundAmountCents = (args: {
  rawAmountCents: string;
  maxAmountCents: number | null;
}): { value?: number; error?: string } => {
  const trimmedAmount = args.rawAmountCents.trim();
  if (trimmedAmount.length === 0) {
    return {};
  }

  if (!/^\d+$/.test(trimmedAmount)) {
    return {
      error: "Refund amount must be a whole number of cents.",
    };
  }

  const parsedAmount = Number(trimmedAmount);
  if (!Number.isSafeInteger(parsedAmount) || parsedAmount <= 0) {
    return {
      error: "Refund amount must be greater than zero.",
    };
  }

  if (
    typeof args.maxAmountCents === "number" &&
    parsedAmount > args.maxAmountCents
  ) {
    return {
      error: "Refund amount cannot exceed the captured transaction amount.",
    };
  }

  return {
    value: parsedAmount,
  };
};

export default function RevenueBillingRoute({
  mode = "billing",
}: RevenueBillingRouteProps) {
  const location = useLocation();
  const { data: meData } = useGetAdminV2MeQuery();
  const { permissionKeySet } = useAdminPermissions(meData?.permissionKeys);
  const [createApproval, createApprovalState] =
    useCreateAdminV2ApprovalMutation({
      fixedCacheKey: REFUND_APPROVAL_FIXED_CACHE_KEY,
    });
  const resetCreateApprovalState = createApprovalState.reset;
  const isWebhookLogsMode = mode === "settings-webhook-logs";
  const routeScopedOrganisationId =
    resolveAdminRevenueAccessScope(location.search)?.organisationId ?? "";
  const [typedOrganisationIdFilter, setTypedOrganisationIdFilter] =
    useState("");
  const [transactionStatusFilter, setTransactionStatusFilter] = useState("");
  const [subscriptionStatusFilter, setSubscriptionStatusFilter] = useState("");
  const [disputeStatusFilter, setDisputeStatusFilter] = useState("");
  const [periodPreset, setPeriodPreset] =
    useState<RevenueBillingPeriodPreset>("month");
  const [webhookStatusFilter, setWebhookStatusFilter] = useState<
    "queued" | "processing" | "processed" | "failed"
  >("failed");
  const [selectedTransactionId, setSelectedTransactionId] = useState<
    string | null
  >(null);
  const [selectedDisputeId, setSelectedDisputeId] = useState<string | null>(
    null,
  );
  const [selectedWebhookEventId, setSelectedWebhookEventId] = useState<
    string | null
  >(null);
  const [selectedScopeKey, setSelectedScopeKey] = useState<string | null>(null);
  const [refundApprovalRequestId, setRefundApprovalRequestId] = useState("");
  const [refundAmountCentsInput, setRefundAmountCentsInput] = useState("");
  const [refundReasonCode, setRefundReasonCode] = useState<
    AdminV2RevenueRefundReason | ""
  >("");
  const [refundInternalNote, setRefundInternalNote] = useState("");
  const [refundWorkflowError, setRefundWorkflowError] = useState<string | null>(
    null,
  );
  const [refundApprovalCreatedId, setRefundApprovalCreatedId] = useState<
    string | null
  >(null);
  const [approvalRequestInFlight, setApprovalRequestInFlight] = useState(false);
  const [subscriptionsPagination, setSubscriptionsPagination] = useState<{
    page: number;
    scopeKey: string | null;
  }>({ page: 0, scopeKey: null });
  const navigationToken = useId();
  const navigationId = `settings-webhook-logs-${navigationToken.replace(
    /:/g,
    "",
  )}`;
  const refundExecutionUnavailableHintId = `${navigationId}-refund-execution-unavailable`;
  const pageStartedAtRef = useRef<number | null>(null);
  const periodPresetRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const approvalRequestInFlightRef = useRef(false);
  const refundContextGenerationRef = useRef(0);
  const webhookReadyLoggedRef = useRef(false);
  const initialWebhookHasOrganisationScopeRef = useRef(
    routeScopedOrganisationId.trim().length > 0,
  );
  const initialWebhookStatusFilterRef = useRef(webhookStatusFilter);
  const approvalRequestIsPending =
    approvalRequestInFlight || createApprovalState.isLoading;

  const resetRefundWorkflowState = () => {
    refundContextGenerationRef.current += 1;
    if (!approvalRequestInFlightRef.current && !createApprovalState.isLoading) {
      resetCreateApprovalState();
    }
    setRefundWorkflowError(null);
    setRefundApprovalCreatedId(null);
    setRefundApprovalRequestId("");
    setRefundAmountCentsInput("");
    setRefundReasonCode("");
    setRefundInternalNote("");
  };

  const handleTransactionSelection = (transactionId: string) => {
    if (activeSelectedTransactionId === transactionId) {
      return;
    }

    resetRefundWorkflowState();
    setSelectedScopeKey(organisationScopeKey);
    setSelectedTransactionId(transactionId);
  };

  const handleTransactionSelectionKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
    transactionId: string,
  ) => {
    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }

    event.preventDefault();
    handleTransactionSelection(transactionId);
  };

  const handlePeriodPresetKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
    currentIndex: number,
  ) => {
    let nextIndex: number | null = null;

    switch (event.key) {
      case "ArrowRight":
      case "ArrowDown":
        nextIndex = (currentIndex + 1) % PERIOD_PRESETS.length;
        break;
      case "ArrowLeft":
      case "ArrowUp":
        nextIndex =
          (currentIndex - 1 + PERIOD_PRESETS.length) % PERIOD_PRESETS.length;
        break;
      case "Home":
        nextIndex = 0;
        break;
      case "End":
        nextIndex = PERIOD_PRESETS.length - 1;
        break;
      default:
        return;
    }

    const nextPreset = PERIOD_PRESETS[nextIndex];
    event.preventDefault();
    setPeriodPreset(nextPreset.value);
    periodPresetRefs.current[nextIndex]?.focus();
  };

  const typedOrganisationIdFilterTrimmed = typedOrganisationIdFilter.trim();
  const typedOrganisationScopeIsValid =
    typedOrganisationIdFilterTrimmed.length === 0 ||
    UUID_LIKE_PATTERN.test(typedOrganisationIdFilterTrimmed);
  const organisationIdFilter =
    typedOrganisationIdFilterTrimmed.length > 0
      ? typedOrganisationIdFilter
      : routeScopedOrganisationId;
  const effectiveOrganisationIdFilter =
    typedOrganisationIdFilterTrimmed.length > 0 && typedOrganisationScopeIsValid
      ? typedOrganisationIdFilterTrimmed
      : routeScopedOrganisationId;
  const trimmedOrganisationIdFilter = effectiveOrganisationIdFilter.trim();
  const organisationScopeIsValid = typedOrganisationScopeIsValid;
  const normalisedOrganisationScope = trimmedOrganisationIdFilter.toLowerCase();
  const organisationScopeKey = organisationScopeIsValid
    ? `valid:${normalisedOrganisationScope || "all"}`
    : "invalid";
  const selectedScopeIsCurrent = selectedScopeKey === organisationScopeKey;
  const activeSelectedTransactionId = selectedScopeIsCurrent
    ? selectedTransactionId
    : null;
  const activeSelectedDisputeId = selectedScopeIsCurrent
    ? selectedDisputeId
    : null;
  const activeSelectedWebhookEventId = selectedScopeIsCurrent
    ? selectedWebhookEventId
    : null;
  const subscriptionsPage =
    subscriptionsPagination.scopeKey === organisationScopeKey
      ? subscriptionsPagination.page
      : 0;
  const setSubscriptionsPage = (
    nextPage: number | ((currentPage: number) => number),
  ) => {
    setSubscriptionsPagination((current) => {
      const currentPage =
        current.scopeKey === organisationScopeKey ? current.page : 0;
      return {
        page: typeof nextPage === "function" ? nextPage(currentPage) : nextPage,
        scopeKey: organisationScopeKey,
      };
    });
  };
  const previousOrganisationScopeKeyRef = useRef(organisationScopeKey);

  useEffect(() => {
    if (previousOrganisationScopeKeyRef.current === organisationScopeKey) {
      return;
    }

    previousOrganisationScopeKeyRef.current = organisationScopeKey;
    refundContextGenerationRef.current += 1;
    if (!approvalRequestInFlightRef.current && !createApprovalState.isLoading) {
      resetCreateApprovalState();
    }
    setSelectedTransactionId(null);
    setSelectedDisputeId(null);
    setSelectedWebhookEventId(null);
    setSelectedScopeKey(null);
    setSubscriptionsPagination({ page: 0, scopeKey: organisationScopeKey });
    setRefundWorkflowError(null);
    setRefundApprovalCreatedId(null);
    setRefundApprovalRequestId("");
    setRefundAmountCentsInput("");
    setRefundReasonCode("");
    setRefundInternalNote("");
  }, [
    createApprovalState.isLoading,
    organisationScopeKey,
    resetCreateApprovalState,
  ]);

  const baseArgs = {
    ...(trimmedOrganisationIdFilter.length > 0
      ? { organisationId: trimmedOrganisationIdFilter }
      : {}),
    limit: 100,
  };

  const transactionsQuery = useGetAdminV2RevenueTransactionsQuery(
    isWebhookLogsMode || !organisationScopeIsValid
      ? skipToken
      : {
          ...baseArgs,
          ...(transactionStatusFilter.trim().length > 0
            ? { status: transactionStatusFilter.trim() }
            : {}),
        },
  );
  const subscriptionsQuery = useGetAdminV2RevenueSubscriptionsQuery(
    isWebhookLogsMode || !organisationScopeIsValid
      ? skipToken
      : {
          ...baseArgs,
          ...(subscriptionStatusFilter.trim().length > 0
            ? { status: subscriptionStatusFilter.trim() }
            : {}),
        },
  );
  const disputesQuery = useGetAdminV2RevenueDisputesQuery(
    isWebhookLogsMode || !organisationScopeIsValid
      ? skipToken
      : {
          ...baseArgs,
          ...(disputeStatusFilter.trim().length > 0
            ? { status: disputeStatusFilter.trim() }
            : {}),
        },
  );
  const webhookEventsQuery = useGetAdminV2RevenueWebhookEventsQuery(
    organisationScopeIsValid
      ? {
          ...baseArgs,
          limit: 50,
          status: webhookStatusFilter,
        }
      : skipToken,
  );
  const transactionDetailQuery = useGetAdminV2RevenueTransactionDetailQuery(
    activeSelectedTransactionId
      ? { transactionId: activeSelectedTransactionId }
      : { transactionId: "" },
    {
      skip:
        isWebhookLogsMode ||
        !organisationScopeIsValid ||
        !activeSelectedTransactionId,
    },
  );
  const disputeDetailQuery = useGetAdminV2RevenueDisputeDetailQuery(
    activeSelectedDisputeId
      ? { disputeId: activeSelectedDisputeId }
      : { disputeId: "" },
    {
      skip:
        isWebhookLogsMode ||
        !organisationScopeIsValid ||
        !activeSelectedDisputeId,
    },
  );
  const webhookDetailQuery = useGetAdminV2RevenueWebhookEventDetailQuery(
    activeSelectedWebhookEventId
      ? { webhookEventId: activeSelectedWebhookEventId }
      : { webhookEventId: "" },
    { skip: !organisationScopeIsValid || !activeSelectedWebhookEventId },
  );

  const currentPeriodStart = useMemo(
    () => getPeriodStart(periodPreset, new Date()),
    [periodPreset],
  );
  const [thirtyDayWindowStart] = useState(
    () => new Date(Date.now() - THIRTY_DAY_WINDOW_MS),
  );

  const transactions = organisationScopeIsValid
    ? (transactionsQuery.currentData?.transactions ?? []).filter((item) =>
        isOnOrAfter(item.occurredAt, currentPeriodStart),
      )
    : [];
  const subscriptions = organisationScopeIsValid
    ? (subscriptionsQuery.currentData?.subscriptions ?? []).filter((item) =>
        isOnOrAfter(
          item.currentPeriodStart ?? item.createdAt,
          currentPeriodStart,
        ),
      )
    : [];
  const disputes = organisationScopeIsValid
    ? (disputesQuery.currentData?.disputes ?? []).filter((item) =>
        isOnOrAfter(item.disputedAt, currentPeriodStart),
      )
    : [];
  const pagedSubscriptions = subscriptions.slice(
    subscriptionsPage * PAGE_SIZE,
    (subscriptionsPage + 1) * PAGE_SIZE,
  );
  const totalSubscriptionPages = Math.max(
    1,
    Math.ceil(subscriptions.length / PAGE_SIZE),
  );
  const webhooks = organisationScopeIsValid
    ? (webhookEventsQuery.currentData?.events ?? EMPTY_WEBHOOK_EVENTS)
    : EMPTY_WEBHOOK_EVENTS;
  const detailMatchesOrganisationScope = (
    detailOrganisationId: string | null | undefined,
  ) =>
    normalisedOrganisationScope.length === 0 ||
    detailOrganisationId?.trim().toLowerCase() === normalisedOrganisationScope;
  const selectedTransactionDetail = transactionDetailQuery.currentData;
  const selectedTransaction =
    organisationScopeIsValid &&
    activeSelectedTransactionId !== null &&
    selectedTransactionDetail?.transaction.id === activeSelectedTransactionId &&
    detailMatchesOrganisationScope(
      selectedTransactionDetail.transaction.organisationId,
    )
      ? selectedTransactionDetail.transaction
      : null;
  const selectedDisputeDetail = disputeDetailQuery.currentData;
  const selectedDispute =
    organisationScopeIsValid &&
    activeSelectedDisputeId !== null &&
    selectedDisputeDetail?.dispute.id === activeSelectedDisputeId &&
    detailMatchesOrganisationScope(selectedDisputeDetail.dispute.organisationId)
      ? selectedDisputeDetail.dispute
      : null;
  const selectedWebhookDetail = webhookDetailQuery.currentData;
  const selectedWebhook =
    organisationScopeIsValid &&
    activeSelectedWebhookEventId !== null &&
    selectedWebhookDetail?.event.id === activeSelectedWebhookEventId &&
    detailMatchesOrganisationScope(selectedWebhookDetail.event.organisationId)
      ? selectedWebhookDetail.event
      : null;
  const canRequestRefundApproval = permissionKeySet.has("approvals.view");
  const selectedTransactionStatus =
    selectedTransaction?.status.trim().toLowerCase() ?? "";
  const selectedTransactionIsRefundable =
    selectedTransaction !== null &&
    REFUNDABLE_TRANSACTION_STATUSES.has(selectedTransactionStatus);
  const purchasesLastThirtyDays =
    organisationScopeIsValid && transactionsQuery.currentData
      ? transactionsQuery.currentData.transactions.filter(
          (item) =>
            item.status.toLowerCase() === "succeeded" &&
            isOnOrAfter(item.occurredAt, thirtyDayWindowStart),
        ).length
      : null;

  const handleRequestRefundApproval = async () => {
    if (approvalRequestInFlightRef.current || createApprovalState.isLoading) {
      return;
    }

    if (!selectedTransaction?.organisationId) {
      setRefundWorkflowError(
        "Refund approval requires a transaction with an organisation id.",
      );
      return;
    }

    if (!selectedTransactionIsRefundable) {
      setRefundWorkflowError(
        "Only succeeded or partially refunded transactions can enter the refund workflow.",
      );
      return;
    }

    if (!canRequestRefundApproval) {
      setRefundWorkflowError(
        "You need approvals access to request refund approval from this page.",
      );
      return;
    }

    const parsedRefundAmount = parseRefundAmountCents({
      rawAmountCents: refundAmountCentsInput,
      maxAmountCents: selectedTransaction.amountCents,
    });
    if (parsedRefundAmount.error) {
      setRefundWorkflowError(parsedRefundAmount.error);
      return;
    }

    approvalRequestInFlightRef.current = true;
    setApprovalRequestInFlight(true);
    setRefundWorkflowError(null);
    setRefundApprovalCreatedId(null);
    const refundContextGeneration = refundContextGenerationRef.current;
    const trimmedInternalNote = refundInternalNote.trim();
    const approvalReason =
      trimmedInternalNote.length > 0
        ? trimmedInternalNote
        : refundReasonCode
          ? `Refund request (${formatVNextLabel(refundReasonCode)}) for ${selectedTransaction.id}`
          : `Refund request for ${selectedTransaction.id}`;

    try {
      const createdApproval = await createApproval({
        organisationId: selectedTransaction.organisationId,
        reason: approvalReason,
        requestType: REFUND_REQUEST_TYPE,
        requiredPermissionKey: "approvals.approve",
        targetId: selectedTransaction.id,
        targetType: "billing_transaction",
        payload: {
          organisationId: selectedTransaction.organisationId,
          transactionId: selectedTransaction.id,
          stripeChargeId: selectedTransaction.stripeChargeId,
          stripePaymentIntentId: selectedTransaction.stripePaymentIntentId,
          ...(parsedRefundAmount.value !== undefined
            ? { amountCents: parsedRefundAmount.value }
            : {}),
          ...(refundReasonCode ? { reason: refundReasonCode } : {}),
          ...(trimmedInternalNote.length > 0
            ? { note: trimmedInternalNote }
            : {}),
          ...(refundApprovalRequestId.trim().length > 0
            ? { approvalRequestId: refundApprovalRequestId.trim() }
            : {}),
        },
      }).unwrap();
      if (refundContextGenerationRef.current !== refundContextGeneration) {
        return;
      }

      setRefundApprovalRequestId(createdApproval.approval.id);
      setRefundApprovalCreatedId(createdApproval.approval.id);
    } catch {
      if (refundContextGenerationRef.current === refundContextGeneration) {
        setRefundWorkflowError("Failed to create the refund approval request.");
      }
    } finally {
      approvalRequestInFlightRef.current = false;
      setApprovalRequestInFlight(false);
    }
  };

  const handleRefreshAll = () => {
    if (!organisationScopeIsValid) {
      return;
    }

    if (!isWebhookLogsMode) {
      void transactionsQuery.refetch();
      void subscriptionsQuery.refetch();
      void disputesQuery.refetch();
    }
    void webhookEventsQuery.refetch();
  };

  const handleOrganisationScopeChange = (nextScope: string) => {
    setTypedOrganisationIdFilter(nextScope);
    setSelectedTransactionId(null);
    setSelectedDisputeId(null);
    setSelectedWebhookEventId(null);
    setSelectedScopeKey(null);
    setSubscriptionsPage(0);
    resetRefundWorkflowState();
  };

  const handleClearOrganisationScope = () => {
    setTypedOrganisationIdFilter("");
    setSelectedTransactionId(null);
    setSelectedDisputeId(null);
    setSelectedWebhookEventId(null);
    setSubscriptionsPage(0);
    resetRefundWorkflowState();
  };

  const activeSubscriptionsSummary =
    !organisationScopeIsValid || !subscriptionsQuery.currentData
      ? adminUiCopy.common.notAvailable
      : subscriptionsQuery.isError
        ? adminUiCopy.common.notAvailable
        : String(
            subscriptions.filter(
              (item) => item.status.toLowerCase() === "active",
            ).length,
          );
  const disputesSummary =
    organisationScopeIsValid && disputesQuery.currentData
      ? String(disputes.length)
      : adminUiCopy.common.notAvailable;
  const getScopeAwareEmptyState = (defaultMessage: string): string =>
    organisationScopeIsValid
      ? defaultMessage
      : INVALID_ORGANISATION_SCOPE_MESSAGE;
  const pageTitle = isWebhookLogsMode ? "Webhook logs" : "Billing overview";
  const pageSubtitle = isWebhookLogsMode
    ? "Review billing webhook failures, retries, and safe event summaries from the Settings workspace."
    : "Review transactions, subscriptions, disputes, and webhook recovery for the selected billing scope.";
  const scopeHelpText = organisationScopeIsValid
    ? isWebhookLogsMode
      ? routeScopedOrganisationId
        ? "This route already carries an organisation scope from the URL. Clear a typed override to return to that webhook scope."
        : "Leave blank to review webhook events across all organisations, or enter a UUID to narrow the log."
      : routeScopedOrganisationId
        ? "This route already carries an organisation scope from the URL. Clear a typed override to return to that billing scope."
        : "Leave blank to review all organisations, or enter a UUID to narrow the billing view."
    : routeScopedOrganisationId
      ? "Enter a valid organisation UUID or clear the override. Billing data is hidden until the scope is valid."
      : "Enter a valid organisation UUID or clear the field. Billing data is hidden until the scope is valid.";
  const scopePlaceholder = "e.g. 123e4567-e89b-12d3-a456-426614174000";
  const webhookSectionLabel = isWebhookLogsMode
    ? "Webhook logs"
    : "Webhook audit";
  const webhookNote = isWebhookLogsMode
    ? "Use this Settings view for webhook delivery and retry checks. Open the billing overview for transactions, subscriptions, disputes, and refund approvals."
    : "Dashboards are backed by billing projection tables populated by webhook processing.";
  const WebhookChrome = isWebhookLogsMode
    ? SettingsWorkspaceChrome
    : RevenueWorkspaceChrome;

  useEffect(() => {
    if (!isWebhookLogsMode) {
      return;
    }

    pageStartedAtRef.current = Date.now();
    webhookReadyLoggedRef.current = false;
    logger.debug("SETTINGS_WEBHOOK_LOGS_PAGE_ENTER", {
      component: "RevenueBillingRoute",
      feature: "ADMIN",
      hasOrganisationScope: initialWebhookHasOrganisationScopeRef.current,
      navigationId,
      operation: "SETTINGS_WEBHOOK_LOGS_PAGE_ENTER",
      statusFilter: initialWebhookStatusFilterRef.current,
    });
  }, [isWebhookLogsMode, navigationId]);

  useEffect(() => {
    if (
      !isWebhookLogsMode ||
      webhookReadyLoggedRef.current ||
      webhookEventsQuery.isLoading
    ) {
      return;
    }

    webhookReadyLoggedRef.current = true;
    logger.debug("SETTINGS_WEBHOOK_LOGS_PAGE_READY", {
      component: "RevenueBillingRoute",
      feature: "ADMIN",
      hasOrganisationScope: trimmedOrganisationIdFilter.length > 0,
      navigationId,
      operation: "SETTINGS_WEBHOOK_LOGS_PAGE_READY",
      pageLoadDurationMs:
        pageStartedAtRef.current === null
          ? null
          : Date.now() - pageStartedAtRef.current,
      resultCount: webhooks.length,
      statusFilter: webhookStatusFilter,
    });
  }, [
    isWebhookLogsMode,
    navigationId,
    trimmedOrganisationIdFilter.length,
    webhookEventsQuery.isLoading,
    webhookStatusFilter,
    webhooks.length,
  ]);

  const trackWebhookFilterChange = (
    nextStatus: "queued" | "processing" | "processed" | "failed",
  ) => {
    if (!isWebhookLogsMode) {
      return;
    }

    logger.debug("SETTINGS_WEBHOOK_LOGS_FILTER_CHANGED", {
      component: "RevenueBillingRoute",
      feature: "ADMIN",
      hasOrganisationScope: trimmedOrganisationIdFilter.length > 0,
      navigationId,
      nextStatusFilter: nextStatus,
      operation: "SETTINGS_WEBHOOK_LOGS_FILTER_CHANGED",
      previousStatusFilter: webhookStatusFilter,
      source: "user",
    });
  };

  const handleWebhookDetailSelection = (eventId: string) => {
    setSelectedScopeKey(organisationScopeKey);
    setSelectedWebhookEventId(eventId);
    setSelectedDisputeId(null);

    if (!isWebhookLogsMode) {
      return;
    }

    logger.debug("SETTINGS_WEBHOOK_LOGS_DETAIL_SELECTED", {
      component: "RevenueBillingRoute",
      feature: "ADMIN",
      hasOrganisationScope: trimmedOrganisationIdFilter.length > 0,
      navigationId,
      operation: "SETTINGS_WEBHOOK_LOGS_DETAIL_SELECTED",
      source: "user",
      statusFilter: webhookStatusFilter,
      webhookEventId: eventId,
    });
  };

  useEffect(() => {
    if (
      !activeSelectedWebhookEventId ||
      webhookEventsQuery.isLoading ||
      webhookEventsQuery.isFetching
    ) {
      return;
    }

    if (!webhooks.some((event) => event.id === activeSelectedWebhookEventId)) {
      setSelectedWebhookEventId(null);
      setSelectedScopeKey(null);
    }
  }, [
    activeSelectedWebhookEventId,
    webhookEventsQuery.isFetching,
    webhookEventsQuery.isLoading,
    webhooks,
  ]);

  return (
    <WebhookChrome subtitle={pageSubtitle} title={pageTitle}>
      <div className="billing-make-page">
        <section className="billing-make-card billing-make-scope-card">
          <div className="billing-make-scope-grid">
            <div className="billing-make-field">
              {routeScopedOrganisationId ? (
                <div className="billing-make-notice">
                  <InfoOutlinedIcon sx={{ fontSize: 14 }} />
                  <span>
                    Billing data is scoped to{" "}
                    <code>
                      {formatMaskedProviderReference(routeScopedOrganisationId)}
                    </code>{" "}
                    from the current URL. Clear scope returns to this
                    organisation unless you intentionally type a different one.
                  </span>
                </div>
              ) : null}
              <label
                htmlFor="billing-org-scope"
                style={{
                  alignItems: "center",
                  display: "inline-flex",
                  gap: 4,
                }}
              >
                <span>Organisation scope</span>
                <InfoTooltip
                  description={
                    adminUiCopy.hoverHelp.revenueBilling.organisationScope
                  }
                  title="Organisation scope"
                />
              </label>
              <input
                aria-describedby="billing-org-scope-help"
                aria-invalid={!organisationScopeIsValid}
                id="billing-org-scope"
                onChange={(event) =>
                  handleOrganisationScopeChange(event.target.value)
                }
                placeholder={scopePlaceholder}
                type="text"
                value={organisationIdFilter}
              />
              <p
                id="billing-org-scope-help"
                role={organisationScopeIsValid ? undefined : "alert"}
              >
                {scopeHelpText}
              </p>
            </div>

            <div className="billing-make-range-panel">
              {!isWebhookLogsMode ? (
                <div className="billing-make-field">
                  <label
                    style={{
                      alignItems: "center",
                      display: "inline-flex",
                      gap: 4,
                    }}
                  >
                    <span id={`${navigationId}-time-range-label`}>
                      Time range
                    </span>
                    <InfoTooltip
                      description={
                        adminUiCopy.hoverHelp.revenueBilling.timeRange
                      }
                      title="Time range"
                    />
                  </label>
                  <div
                    aria-labelledby={`${navigationId}-time-range-label`}
                    className="billing-make-range-group"
                    role="radiogroup"
                  >
                    {PERIOD_PRESETS.map((preset, index) => (
                      <button
                        aria-checked={periodPreset === preset.value}
                        className={`billing-make-range-chip${
                          periodPreset === preset.value ? " is-active" : ""
                        }`}
                        key={preset.value}
                        onClick={() => setPeriodPreset(preset.value)}
                        onKeyDown={(event) =>
                          handlePeriodPresetKeyDown(event, index)
                        }
                        ref={(element) => {
                          periodPresetRefs.current[index] = element;
                        }}
                        role="radio"
                        tabIndex={periodPreset === preset.value ? 0 : -1}
                        type="button"
                      >
                        {preset.label}
                      </button>
                    ))}
                  </div>
                  <p className="billing-make-note">
                    Time range updates the summary cards and the list data
                    below.
                  </p>
                </div>
              ) : null}

              <button
                className="billing-make-refresh-button"
                disabled={typedOrganisationIdFilter.trim().length === 0}
                onClick={handleClearOrganisationScope}
                type="button"
              >
                <span>Clear scope</span>
              </button>

              <button
                className="billing-make-refresh-button"
                disabled={!organisationScopeIsValid}
                onClick={handleRefreshAll}
                title={adminUiCopy.hoverHelp.revenueBilling.refresh}
                type="button"
              >
                <AutorenewOutlinedIcon sx={{ fontSize: 13 }} />
                <span>
                  {isWebhookLogsMode
                    ? "Refresh webhook logs"
                    : "Refresh billing data"}
                </span>
              </button>
            </div>
          </div>
        </section>

        {isWebhookLogsMode ? (
          <div className="billing-make-notice">
            <InfoOutlinedIcon sx={{ fontSize: 14 }} />
            <span>
              {webhookNote}{" "}
              <Link
                to={buildAdminRevenueScopedPath(
                  "/revenue/billing",
                  location.search,
                )}
              >
                Open billing overview.
              </Link>
            </span>
          </div>
        ) : null}

        {!isWebhookLogsMode && refundWorkflowError ? (
          <div className="billing-make-notice" role="alert">
            <InfoOutlinedIcon sx={{ fontSize: 14 }} />
            <span>{refundWorkflowError}</span>
          </div>
        ) : null}

        {!isWebhookLogsMode &&
        organisationScopeIsValid &&
        selectedTransaction &&
        refundApprovalCreatedId ? (
          <div className="billing-make-notice billing-make-notice--success">
            <InfoOutlinedIcon sx={{ fontSize: 14 }} />
            <span>
              Refund approval created: {refundApprovalCreatedId}.{" "}
              <Link to="/settings/approvals">Open approvals queue</Link> to
              approve it. Refund execution is handled outside this Billing
              overview.
            </span>
          </div>
        ) : null}

        {!isWebhookLogsMode ? (
          <>
            <div className="billing-make-summary-chips">
              <div className="billing-make-summary-chip">
                {InlineHelpLabel({
                  label: "Active subscriptions shown:",
                  title: "Active subscriptions shown",
                  description:
                    adminUiCopy.hoverHelp.revenueBilling
                      .activeSubscriptionsSummary,
                })}
                <strong>{activeSubscriptionsSummary}</strong>
                <small>
                  {
                    adminUiCopy.hoverHelp.revenueBilling
                      .activeSubscriptionsContext
                  }
                </small>
              </div>
              <div className="billing-make-summary-chip">
                {InlineHelpLabel({
                  label: "Succeeded purchases shown (30 days):",
                  title: "Succeeded purchases shown in the last 30 days",
                  description:
                    adminUiCopy.hoverHelp.revenueBilling
                      .purchasesLastThirtyDays,
                })}
                <strong>
                  {purchasesLastThirtyDays ?? adminUiCopy.common.notAvailable}
                </strong>
                <small>
                  {
                    adminUiCopy.hoverHelp.revenueBilling
                      .purchasesLastThirtyDaysContext
                  }
                </small>
              </div>
              <div className="billing-make-summary-chip">
                {InlineHelpLabel({
                  label: "Disputes shown:",
                  title: "Disputes shown",
                  description:
                    adminUiCopy.hoverHelp.revenueBilling.disputesSummary,
                })}
                <strong>{disputesSummary}</strong>
                <small>
                  {adminUiCopy.hoverHelp.revenueBilling.disputesContext}
                </small>
              </div>
            </div>

            <section
              aria-label="Transactions"
              className="billing-make-card billing-make-section"
            >
              <div className="billing-make-section__header">
                <h3>Transactions</h3>
              </div>
              <div className="billing-make-section__filters">
                <label
                  style={{
                    alignItems: "center",
                    display: "inline-flex",
                    gap: 4,
                  }}
                >
                  <span>Transaction status</span>
                  <InfoTooltip
                    description={
                      adminUiCopy.hoverHelp.revenueBilling.transactionStatus
                    }
                    title="Transactions status filter"
                  />
                </label>
                <input
                  aria-label="Transactions status filter"
                  onChange={(event) =>
                    setTransactionStatusFilter(event.target.value)
                  }
                  placeholder="optional status filter"
                  type="text"
                  value={transactionStatusFilter}
                />
              </div>
              {transactions.length === 0 ? (
                <div className="billing-make-section__body">
                  <BillingEmptyState
                    message={getScopeAwareEmptyState(
                      "No transactions matched the current scope.",
                    )}
                  />
                </div>
              ) : (
                <div className="billing-make-list">
                  {transactions.map((transaction) => (
                    <button
                      aria-label={formatTransactionAccessibleName(transaction)}
                      className={`billing-make-list__row${
                        activeSelectedTransactionId === transaction.id
                          ? " is-selected"
                          : ""
                      }`}
                      key={transaction.id}
                      onClick={() => handleTransactionSelection(transaction.id)}
                      onKeyDown={(event) =>
                        handleTransactionSelectionKeyDown(event, transaction.id)
                      }
                      type="button"
                    >
                      <div className="billing-make-list__row-main">
                        <div className="billing-make-list__title-row">
                          <span className="billing-make-list__title">
                            {formatMaskedProviderReference(transaction.id)}
                          </span>
                          <span
                            className={`billing-make-status-pill is-${getStatusTone(
                              transaction.status,
                            )}`}
                          >
                            {formatVNextLabel(transaction.status)}
                          </span>
                        </div>
                        <div className="billing-make-list__meta billing-make-list__meta--mono">
                          <span
                            title={
                              adminUiCopy.hoverHelp.revenueBillingTerms
                                .paymentIntent
                            }
                          >
                            Payment ref:
                          </span>
                          <strong>
                            {formatMaskedProviderReference(
                              transaction.stripePaymentIntentId,
                            )}
                          </strong>
                          <span>|</span>
                          <span
                            title={
                              adminUiCopy.hoverHelp.revenueBillingTerms.charge
                            }
                          >
                            Charge ref:
                          </span>
                          <strong>
                            {formatMaskedProviderReference(
                              transaction.stripeChargeId,
                            )}
                          </strong>
                          <span>|</span>
                          <span>Organisation:</span>
                          <strong>
                            {formatMaskedProviderReference(
                              transaction.organisationId,
                            )}
                          </strong>
                        </div>
                      </div>
                      <div className="billing-make-list__row-side">
                        <strong>
                          {formatVNextCurrency(
                            transaction.amountCents,
                            transaction.currency,
                          )}
                        </strong>
                        <span>
                          {formatVNextDateTime(transaction.occurredAt)}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </section>

            <section
              aria-label="Subscriptions"
              className="billing-make-card billing-make-section"
            >
              <div className="billing-make-section__header billing-make-section__header--split">
                <h3>Subscriptions</h3>
                <div className="billing-make-inline-actions">
                  <button disabled type="button">
                    <SubjectOutlinedIcon sx={{ fontSize: 12 }} />
                    <span>Audit trail</span>
                  </button>
                  <button className="is-primary" disabled type="button">
                    <SmartToyOutlinedIcon sx={{ fontSize: 12 }} />
                    <span>+ Assistant</span>
                  </button>
                </div>
              </div>
              <div className="billing-make-section__filters">
                <label
                  style={{
                    alignItems: "center",
                    display: "inline-flex",
                    gap: 4,
                  }}
                >
                  <span>Subscription status</span>
                  <InfoTooltip
                    description={
                      adminUiCopy.hoverHelp.revenueBilling.subscriptionStatus
                    }
                    title="Subscriptions status filter"
                  />
                </label>
                <input
                  aria-label="Subscriptions status filter"
                  onChange={(event) => {
                    setSubscriptionStatusFilter(event.target.value);
                    setSubscriptionsPage(0);
                  }}
                  placeholder="optional status filter"
                  type="text"
                  value={subscriptionStatusFilter}
                />
              </div>
              {subscriptionsQuery.isError ? (
                <div className="billing-make-section__body">
                  <BillingEmptyState message="Failed to load subscriptions." />
                </div>
              ) : pagedSubscriptions.length === 0 ? (
                <div className="billing-make-section__body">
                  <BillingEmptyState
                    message={getScopeAwareEmptyState(
                      "No subscriptions matched the current scope.",
                    )}
                  />
                </div>
              ) : (
                <>
                  <div className="billing-make-list">
                    {pagedSubscriptions.map((subscription) => {
                      const trimmedProductName =
                        subscription.productName?.trim();
                      const subscriptionTitle =
                        trimmedProductName && trimmedProductName.length > 0
                          ? trimmedProductName
                          : "Unknown plan";

                      return (
                        <div
                          className="billing-make-list__row billing-make-list__row--static"
                          key={subscription.stripeSubscriptionId}
                        >
                          <div className="billing-make-list__row-main">
                            <div className="billing-make-list__title-row">
                              <span className="billing-make-list__title">
                                {subscriptionTitle}
                              </span>
                              <span
                                className={`billing-make-status-pill is-${getStatusTone(
                                  subscription.status,
                                )}`}
                              >
                                {formatVNextLabel(subscription.status)}
                              </span>
                            </div>
                            <div className="billing-make-list__meta billing-make-list__meta--mono">
                              <span>Subscription:</span>
                              <strong>
                                {formatMaskedProviderReference(
                                  subscription.stripeSubscriptionId,
                                )}
                              </strong>
                              <span>|</span>
                              <span
                                title={
                                  adminUiCopy.hoverHelp.revenueBillingTerms
                                    .mappedEntity
                                }
                              >
                                Billing entity:
                              </span>
                              <strong>
                                {`${formatVNextLabel(
                                  subscription.mappedEntityType,
                                )} · ${formatMaskedProviderReference(
                                  subscription.mappedEntityId,
                                )}`}
                              </strong>
                              <span>|</span>
                              <span
                                title={
                                  adminUiCopy.hoverHelp.revenueBillingTerms
                                    .stripeCustomer
                                }
                              >
                                Customer ref:
                              </span>
                              <strong>
                                {formatMaskedProviderReference(
                                  subscription.stripeCustomerId,
                                )}
                              </strong>
                            </div>
                          </div>
                          <div className="billing-make-list__row-side">
                            <strong>
                              {formatVNextCurrency(
                                subscription.mrrCents,
                                subscription.currency ?? "USD",
                              )}
                            </strong>
                            <span>
                              {formatVNextDateTime(
                                subscription.currentPeriodEnd,
                              )}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {subscriptions.length > PAGE_SIZE ? (
                    <div className="billing-make-pagination">
                      <div className="billing-make-pagination__controls">
                        <button
                          aria-label="Previous page"
                          disabled={subscriptionsPage === 0}
                          onClick={() =>
                            setSubscriptionsPage((value) =>
                              Math.max(0, value - 1),
                            )
                          }
                          type="button"
                        >
                          Previous page
                        </button>
                        <span>
                          {subscriptionsPage + 1} / {totalSubscriptionPages}
                        </span>
                        <button
                          aria-label="Next page"
                          disabled={
                            subscriptionsPage >= totalSubscriptionPages - 1
                          }
                          onClick={() =>
                            setSubscriptionsPage((value) =>
                              Math.min(totalSubscriptionPages - 1, value + 1),
                            )
                          }
                          type="button"
                        >
                          Next page
                        </button>
                      </div>
                      <span>
                        Showing {subscriptionsPage * PAGE_SIZE + 1}–
                        {Math.min(
                          (subscriptionsPage + 1) * PAGE_SIZE,
                          subscriptions.length,
                        )}{" "}
                        of {subscriptions.length}
                      </span>
                    </div>
                  ) : null}
                </>
              )}
            </section>

            <section
              aria-label="Disputes"
              className="billing-make-card billing-make-section"
            >
              <div className="billing-make-section__header">
                <h3>Disputes</h3>
              </div>
              <div className="billing-make-section__filters">
                <label
                  style={{
                    alignItems: "center",
                    display: "inline-flex",
                    gap: 4,
                  }}
                >
                  <span>Dispute status</span>
                  <InfoTooltip
                    description={
                      adminUiCopy.hoverHelp.revenueBilling.disputeStatus
                    }
                    title="Disputes status filter"
                  />
                </label>
                <input
                  aria-label="Disputes status filter"
                  onChange={(event) =>
                    setDisputeStatusFilter(event.target.value)
                  }
                  placeholder="optional status filter"
                  type="text"
                  value={disputeStatusFilter}
                />
              </div>
              {disputes.length === 0 ? (
                <div className="billing-make-section__body">
                  <BillingEmptyState
                    message={getScopeAwareEmptyState(
                      "No disputes matched the current scope.",
                    )}
                  />
                </div>
              ) : (
                <div className="billing-make-list">
                  {disputes.map((dispute) => (
                    <button
                      className={`billing-make-list__row${
                        activeSelectedDisputeId === dispute.id
                          ? " is-selected"
                          : ""
                      }`}
                      key={dispute.id}
                      onClick={() => {
                        setSelectedScopeKey(organisationScopeKey);
                        setSelectedDisputeId(dispute.id);
                        setSelectedWebhookEventId(null);
                      }}
                      type="button"
                    >
                      <div className="billing-make-list__row-main">
                        <div className="billing-make-list__title-row">
                          <span className="billing-make-list__title">
                            {formatMaskedProviderReference(
                              dispute.stripeDisputeId,
                            )}
                          </span>
                          <span
                            className={`billing-make-status-pill is-${getStatusTone(
                              dispute.status,
                            )}`}
                          >
                            {formatVNextLabel(dispute.status)}
                          </span>
                        </div>
                        <div className="billing-make-list__meta">
                          <span>
                            {dispute.reason ?? adminUiCopy.common.notAvailable}
                          </span>
                        </div>
                      </div>
                      <div className="billing-make-list__row-side">
                        <strong>
                          {formatVNextCurrency(
                            dispute.amountCents,
                            dispute.currency,
                          )}
                        </strong>
                        <span>{formatVNextDateTime(dispute.disputedAt)}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </section>
          </>
        ) : null}

        <section
          aria-label={webhookSectionLabel}
          className="billing-make-card billing-make-section"
        >
          <div className="billing-make-section__header">
            <h3>{webhookSectionLabel}</h3>
          </div>
          <div className="billing-make-section__filters">
            <label className="billing-make-webhook-filter">
              <span>Webhook status</span>
              <InfoTooltip
                description={adminUiCopy.hoverHelp.revenueBilling.webhookStatus}
                title="Webhook status filter"
              />
              <select
                aria-label="Webhook status filter"
                onChange={(event) => {
                  const nextStatus = event.target.value as
                    "queued" | "processing" | "processed" | "failed";
                  setSelectedWebhookEventId(null);
                  setWebhookStatusFilter(nextStatus);
                  trackWebhookFilterChange(nextStatus);
                }}
                value={webhookStatusFilter}
              >
                <option value="failed">failed</option>
                <option value="processing">processing</option>
                <option value="processed">processed</option>
                <option value="queued">queued</option>
              </select>
              <ExpandMoreOutlinedIcon sx={{ fontSize: 13 }} />
            </label>
          </div>
          {webhooks.length === 0 ? (
            <div className="billing-make-section__body">
              <BillingEmptyState
                message={getScopeAwareEmptyState(
                  "No webhook events found for the selected filter.",
                )}
              />
            </div>
          ) : (
            <div className="billing-make-list">
              {webhooks.map((event) => (
                <button
                  className={`billing-make-list__row${
                    activeSelectedWebhookEventId === event.id
                      ? " is-selected"
                      : ""
                  }`}
                  key={event.id}
                  onClick={() => handleWebhookDetailSelection(event.id)}
                  type="button"
                >
                  <div className="billing-make-list__row-main">
                    <div className="billing-make-list__title-row">
                      <span className="billing-make-list__title">
                        {event.eventType}
                      </span>
                      <span
                        className={`billing-make-status-pill is-${getStatusTone(
                          event.status,
                        )}`}
                      >
                        {formatVNextLabel(event.status)}
                      </span>
                    </div>
                    <div className="billing-make-list__meta billing-make-list__meta--mono">
                      <span>attempts:</span>
                      <strong>{formatVNextNumber(event.attempts)}</strong>
                      <span>|</span>
                      <span>event:</span>
                      <strong>
                        {formatMaskedProviderReference(event.stripeEventId)}
                      </strong>
                    </div>
                  </div>
                  <div className="billing-make-list__row-side">
                    <strong>{formatWebhookRowFailureSummary(event)}</strong>
                    <span>{formatVNextDateTime(event.processedAt)}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </section>

        <div className="billing-make-divider" />

        <p className="billing-make-note">{webhookNote}</p>

        {!isWebhookLogsMode ? (
          <div className="billing-make-detail-grid">
            <section
              className="billing-make-card billing-make-detail-card"
              aria-label="Transaction detail"
            >
              <div className="billing-make-section__header">
                <h3>Transaction detail</h3>
              </div>
              <div className="billing-make-detail-card__body">
                {selectedTransaction ? (
                  <>
                    {makeDetailItems([
                      {
                        label: "Transaction ID",
                        value: formatMaskedProviderReference(
                          selectedTransaction.id,
                        ),
                      },
                      {
                        label: "Organisation",
                        value:
                          selectedTransactionDetail?.linkedCrm
                            ?.organisationName ??
                          formatMaskedProviderReference(
                            selectedTransaction.organisationId,
                          ),
                      },
                      {
                        label: "Amount",
                        value: formatVNextCurrency(
                          selectedTransaction.amountCents,
                          selectedTransaction.currency,
                        ),
                      },
                      {
                        label: "Occurred",
                        value: formatVNextDateTime(
                          selectedTransaction.occurredAt,
                        ),
                      },
                      {
                        label: "Charge reference",
                        value: formatMaskedProviderReference(
                          selectedTransaction.stripeChargeId,
                        ),
                      },
                    ])}
                    <div className="billing-make-field billing-make-field--detail">
                      <label htmlFor="billing-refund-approval-id">
                        {InlineHelpLabel({
                          label: "Refund approval request ID",
                          title: "Refund approval request ID",
                          description:
                            adminUiCopy.hoverHelp.revenueBilling
                              .refundApprovalRequestId,
                        })}
                      </label>
                      <input
                        id="billing-refund-approval-id"
                        onChange={(event) =>
                          setRefundApprovalRequestId(event.target.value)
                        }
                        type="text"
                        value={refundApprovalRequestId}
                      />
                      <p>
                        Optional approval request id for environments that
                        require a linked request.
                      </p>
                    </div>
                    <div className="billing-make-field billing-make-field--detail">
                      <label htmlFor="billing-refund-amount-cents">
                        {InlineHelpLabel({
                          label: "Refund amount (cents)",
                          title: "Refund amount",
                          description:
                            adminUiCopy.hoverHelp.revenueBilling.refundAmount,
                        })}
                      </label>
                      <input
                        id="billing-refund-amount-cents"
                        inputMode="numeric"
                        onChange={(event) =>
                          setRefundAmountCentsInput(event.target.value)
                        }
                        placeholder="Leave blank for a full refund"
                        type="text"
                        value={refundAmountCentsInput}
                      />
                      <p>
                        Leave blank to refund the full captured amount, or enter
                        a smaller amount in cents for a partial refund. When you
                        use an approved request id, the amount and Stripe reason
                        must match that approved request.
                      </p>
                    </div>
                    <div className="billing-make-field billing-make-field--detail">
                      <label htmlFor="billing-refund-reason-code">
                        {InlineHelpLabel({
                          label: "Stripe refund reason",
                          title: "Stripe refund reason",
                          description:
                            adminUiCopy.hoverHelp.revenueBilling.refundReason,
                        })}
                      </label>
                      <select
                        id="billing-refund-reason-code"
                        onChange={(event) =>
                          setRefundReasonCode(
                            event.target.value as
                              AdminV2RevenueRefundReason | "",
                          )
                        }
                        value={refundReasonCode}
                      >
                        <option value="">
                          Leave blank unless Stripe needs a reason
                        </option>
                        {REFUND_REASON_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <p>
                        Optional. Stripe accepts only Duplicate, Fraudulent, or
                        Requested by customer.
                      </p>
                    </div>
                    <div className="billing-make-field billing-make-field--detail">
                      <label htmlFor="billing-refund-reason">
                        {InlineHelpLabel({
                          label: "Internal refund note",
                          title: "Internal refund note",
                          description:
                            adminUiCopy.hoverHelp.revenueBilling
                              .refundInternalNote,
                        })}
                      </label>
                      <textarea
                        id="billing-refund-reason"
                        onChange={(event) =>
                          setRefundInternalNote(event.target.value)
                        }
                        rows={3}
                        value={refundInternalNote}
                      />
                      <p>
                        Shared with approvers and stored as refund metadata.
                        This does not replace Stripe&apos;s reason code.
                      </p>
                    </div>
                    <div className="billing-make-detail-card__actions">
                      <button
                        disabled={
                          approvalRequestIsPending ||
                          !canRequestRefundApproval ||
                          !selectedTransactionIsRefundable
                        }
                        onClick={() => void handleRequestRefundApproval()}
                        type="button"
                      >
                        Request refund approval
                      </button>
                      <button
                        aria-describedby={refundExecutionUnavailableHintId}
                        disabled
                        type="button"
                      >
                        Execute approved refund
                      </button>
                      {approvalRequestIsPending ? (
                        <span
                          className="billing-make-detail-card__hint"
                          role="status"
                        >
                          {REFUND_APPROVAL_PENDING_MESSAGE}
                        </span>
                      ) : null}
                      {!selectedTransactionIsRefundable ? (
                        <span className="billing-make-detail-card__hint">
                          Refunds are available only for succeeded or partially
                          refunded transactions.
                        </span>
                      ) : null}
                      {!canRequestRefundApproval ? (
                        <span className="billing-make-detail-card__hint">
                          You need approvals access to request refund approval
                          from this page.
                        </span>
                      ) : null}
                      <span
                        className="billing-make-detail-card__hint"
                        id={refundExecutionUnavailableHintId}
                      >
                        {REFUND_EXECUTION_UNAVAILABLE_REASON}
                      </span>
                    </div>
                  </>
                ) : (
                  <BillingEmptyState
                    message={getScopeAwareEmptyState(
                      "Select a transaction row to inspect linked CRM and refund approval details.",
                    )}
                  />
                )}
              </div>
            </section>

            <section
              className="billing-make-card billing-make-detail-card"
              aria-label="Dispute and webhook detail"
            >
              <div className="billing-make-section__header">
                <h3>Dispute and webhook detail</h3>
              </div>
              <div className="billing-make-detail-card__body">
                {selectedDispute ? (
                  makeDetailItems([
                    {
                      label: "Dispute reference",
                      value: formatMaskedProviderReference(
                        selectedDispute.stripeDisputeId,
                      ),
                    },
                    {
                      label: "Status",
                      value: formatVNextLabel(selectedDispute.status),
                    },
                    {
                      label: "Evidence due",
                      value: formatVNextDateTime(selectedDispute.evidenceDueBy),
                    },
                    {
                      label: "Checklist path",
                      value:
                        selectedDisputeDetail?.disputeEvidenceChecklistPath ??
                        "n/a",
                    },
                  ])
                ) : selectedWebhook ? (
                  makeWebhookSafeEventSummary(selectedWebhook)
                ) : (
                  <BillingEmptyState
                    message={getScopeAwareEmptyState(
                      "Select a dispute or webhook row to inspect the failure context.",
                    )}
                  />
                )}
              </div>
            </section>
          </div>
        ) : (
          <section
            aria-label="Webhook detail"
            className="billing-make-card billing-make-detail-card"
          >
            <div className="billing-make-section__header">
              <h3>Webhook detail</h3>
            </div>
            <div className="billing-make-detail-card__body">
              {selectedWebhook ? (
                makeWebhookSafeEventSummary(selectedWebhook)
              ) : (
                <BillingEmptyState
                  message={getScopeAwareEmptyState(
                    "Select a webhook row to inspect the failure context.",
                  )}
                />
              )}
            </div>
          </section>
        )}
      </div>
    </WebhookChrome>
  );
}
